// ============================================================================
// TIME TRACKER API CLIENT
// ============================================================================
// Endpoint helpers for Clients, Projects, TaskTypes, Tasks, TimeEntries.
// Uses the shared axiosClient — auth token and tenant headers are attached by
// existing interceptors. All responses are unwrapped from ApiResponse<T>.data.

import axiosClient from '../client/axiosClient';

// ============================================================================
// Shared response shapes (mirror server-side ApiResponse<T>, PagedResult<T>)
// ============================================================================

export interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
  errors?: string[];
  correlationId?: string;
  timestamp?: string;
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface PageQuery {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  sortBy?: string;
  sortOrder?: string;
}

// ============================================================================
// Reports
// ============================================================================

export interface ReportQuery {
  /** ISO date (YYYY-MM-DD) — inclusive lower bound on entry_date. */
  dateFrom?: string;
  /** ISO date (YYYY-MM-DD) — inclusive upper bound on entry_date. */
  dateTo?: string;
  userId?: string;
  clientId?: string;
  taskId?: string;
}

export interface ReportSummaryDto {
  totalHours: number;
  entryCount: number;
  userCount: number;
  taskCount: number;
  clientCount: number;
  /** Sum of TotalAmount across all report rows. */
  totalAmount: number;
}

/** One flat row in the time report: aggregated hours per (user, task) tuple. */
export interface ReportRowDto {
  userId: string;
  userName: string;
  clientId: string;
  clientName: string;
  taskId: string;
  taskTitle: string;
  totalHours: number;
  entryCount: number;
  /** Billed amount = sum(entry.hours * effective_rate_at_entry_date). 0 when no rate. */
  totalAmount: number;
}

export interface ReportResponseDto {
  summary: ReportSummaryDto;
  rows: ReportRowDto[];
  dateFrom?: string | null;
  dateTo?: string | null;
}

export const reportsApi = {
  time: async (q: ReportQuery) => {
    const res = await axiosClient.get<ApiEnvelope<ReportResponseDto>>('/v1/reports/time', { params: q });
    return res.data.data;
  },
};

// ============================================================================
// Users (admin-only)
// ============================================================================

export interface UserDto {
  id: string;
  username: string;
  email: string;
  fullName: string;
  role: 'Admin' | 'User' | string;
  isActive: boolean;
  lastLoginAt?: string | null;
  createdAt: string;
  /** Hourly billing rate effective today; null if none configured. */
  currentHourlyRate?: number | null;
}

export interface UserRateDto {
  id: string;
  userId: string;
  hourlyRate: number;
  /** Inclusive start date (YYYY-MM-DD). */
  effectiveFrom: string;
  /** Inclusive end date; null = currently in effect. */
  effectiveTo?: string | null;
  note?: string | null;
  rowVersion: number;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

export interface CreateUserRateRequest {
  hourlyRate: number;
  effectiveFrom?: string | null;
  note?: string | null;
}

export interface UpdateUserRateRequest {
  hourlyRate: number;
  note?: string | null;
  rowVersion: number;
}

export interface CreateUserRequest {
  username: string;
  email: string;
  fullName: string;
  password: string;
  role: 'Admin' | 'User';
  isActive: boolean;
}

export interface UpdateUserRequest {
  email: string;
  fullName: string;
  role: 'Admin' | 'User';
  isActive: boolean;
}

export interface ResetPasswordRequest {
  newPassword: string;
}

export interface UserQuery extends PageQuery {
  role?: 'Admin' | 'User';
  includeInactive?: boolean;
}

export const userApi = {
  list: async (q: UserQuery = {}) => {
    const res = await axiosClient.get<ApiEnvelope<PagedResult<UserDto>>>('/v1/local-users', { params: q });
    return res.data.data;
  },
  getById: async (id: string) => {
    const res = await axiosClient.get<ApiEnvelope<UserDto>>(`/v1/local-users/${id}`);
    return res.data.data;
  },
  create: async (req: CreateUserRequest) => {
    const res = await axiosClient.post<ApiEnvelope<UserDto>>('/v1/local-users', req);
    return res.data.data;
  },
  update: async (id: string, req: UpdateUserRequest) => {
    const res = await axiosClient.put<ApiEnvelope<UserDto>>(`/v1/local-users/${id}`, req);
    return res.data.data;
  },
  remove: async (id: string) => {
    await axiosClient.delete(`/v1/local-users/${id}`);
  },
  resetPassword: async (id: string, newPassword: string) => {
    await axiosClient.post(`/v1/local-users/${id}/reset-password`, { newPassword });
  },
};

export const userRateApi = {
  list: async (userId: string) => {
    const res = await axiosClient.get<ApiEnvelope<UserRateDto[]>>(`/v1/local-users/${userId}/rates`);
    return res.data.data;
  },
  create: async (userId: string, req: CreateUserRateRequest) => {
    const res = await axiosClient.post<ApiEnvelope<UserRateDto>>(`/v1/local-users/${userId}/rates`, req);
    return res.data.data;
  },
  update: async (userId: string, id: string, req: UpdateUserRateRequest) => {
    const res = await axiosClient.put<ApiEnvelope<UserRateDto>>(`/v1/local-users/${userId}/rates/${id}`, req);
    return res.data.data;
  },
  remove: async (userId: string, id: string, rowVersion: number) => {
    await axiosClient.delete(`/v1/local-users/${userId}/rates/${id}`, { params: { rowVersion } });
  },
};

// ============================================================================
// Client
// ============================================================================

export interface ClientDto {
  id: string;
  name: string;
  contactEmail?: string | null;
  contactPhone?: string | null;
  address?: string | null;
  isActive: boolean;
  rowVersion: number;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

export interface ClientUpsertRequest {
  name: string;
  contactEmail?: string | null;
  contactPhone?: string | null;
  address?: string | null;
  isActive: boolean;
  rowVersion?: number;
}

export interface ClientQuery extends PageQuery {
  name?: string;
  includeInactive?: boolean;
}

export const clientApi = {
  list: async (q: ClientQuery = {}) => {
    const res = await axiosClient.get<ApiEnvelope<PagedResult<ClientDto>>>('/v1/clients', { params: q });
    return res.data.data;
  },
  getById: async (id: string) => {
    const res = await axiosClient.get<ApiEnvelope<ClientDto>>(`/v1/clients/${id}`);
    return res.data.data;
  },
  create: async (req: ClientUpsertRequest) => {
    const res = await axiosClient.post<ApiEnvelope<ClientDto>>('/v1/clients', req);
    return res.data.data;
  },
  update: async (id: string, req: ClientUpsertRequest & { rowVersion: number }) => {
    const res = await axiosClient.put<ApiEnvelope<ClientDto>>(`/v1/clients/${id}`, req);
    return res.data.data;
  },
  remove: async (id: string, rowVersion: number) => {
    await axiosClient.delete(`/v1/clients/${id}`, { params: { rowVersion } });
  },
};

// ============================================================================
// Project (standalone master — no longer linked to Client)
// ============================================================================

export interface StatusHistoryEntryDto {
  id: string;
  /** 'Closed' or 'Reopened' */
  transition: 'Closed' | 'Reopened';
  changedAt: string;
  changedById?: string | null;
  changedByName?: string | null;
  note?: string | null;
}

export interface ProjectDto {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  status: 'Open' | 'Closed';
  closedUserId?: string | null;
  closedUserName?: string | null;
  closedAt?: string | null;
  rowVersion: number;
  createdAt: string;
  updatedAt?: string | null;
  /** Populated by GET /v1/projects/{id} only — empty in list responses. */
  statusHistory?: StatusHistoryEntryDto[];
}

export interface ProjectUpsertRequest {
  name: string;
  description?: string | null;
  isActive: boolean;
  rowVersion?: number;
}

export interface ProjectQuery extends PageQuery {
  name?: string;
  includeInactive?: boolean;
  status?: 'Open' | 'Closed';
}

export const projectApi = {
  list: async (q: ProjectQuery = {}) => {
    const res = await axiosClient.get<ApiEnvelope<PagedResult<ProjectDto>>>('/v1/projects', { params: q });
    return res.data.data;
  },
  getById: async (id: string) => {
    const res = await axiosClient.get<ApiEnvelope<ProjectDto>>(`/v1/projects/${id}`);
    return res.data.data;
  },
  create: async (req: ProjectUpsertRequest) => {
    const res = await axiosClient.post<ApiEnvelope<ProjectDto>>('/v1/projects', req);
    return res.data.data;
  },
  update: async (id: string, req: ProjectUpsertRequest & { rowVersion: number }) => {
    const res = await axiosClient.put<ApiEnvelope<ProjectDto>>(`/v1/projects/${id}`, req);
    return res.data.data;
  },
  remove: async (id: string, rowVersion: number) => {
    await axiosClient.delete(`/v1/projects/${id}`, { params: { rowVersion } });
  },
  close: async (id: string, rowVersion: number) => {
    const res = await axiosClient.patch<ApiEnvelope<ProjectDto>>(`/v1/projects/${id}/close`, null, { params: { rowVersion } });
    return res.data.data;
  },
  reopen: async (id: string, rowVersion: number) => {
    const res = await axiosClient.patch<ApiEnvelope<ProjectDto>>(`/v1/projects/${id}/reopen`, null, { params: { rowVersion } });
    return res.data.data;
  },
};

// ============================================================================
// TaskType
// ============================================================================

export interface TaskTypeDto {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  rowVersion: number;
  createdAt: string;
  updatedAt?: string | null;
}

export interface TaskTypeUpsertRequest {
  name: string;
  description?: string | null;
  isActive: boolean;
  rowVersion?: number;
}

export interface TaskTypeQuery extends PageQuery {
  name?: string;
  includeInactive?: boolean;
}

export const taskTypeApi = {
  list: async (q: TaskTypeQuery = {}) => {
    const res = await axiosClient.get<ApiEnvelope<PagedResult<TaskTypeDto>>>('/v1/task-types', { params: q });
    return res.data.data;
  },
  getById: async (id: string) => {
    const res = await axiosClient.get<ApiEnvelope<TaskTypeDto>>(`/v1/task-types/${id}`);
    return res.data.data;
  },
  create: async (req: TaskTypeUpsertRequest) => {
    const res = await axiosClient.post<ApiEnvelope<TaskTypeDto>>('/v1/task-types', req);
    return res.data.data;
  },
  update: async (id: string, req: TaskTypeUpsertRequest & { rowVersion: number }) => {
    const res = await axiosClient.put<ApiEnvelope<TaskTypeDto>>(`/v1/task-types/${id}`, req);
    return res.data.data;
  },
  remove: async (id: string, rowVersion: number) => {
    await axiosClient.delete(`/v1/task-types/${id}`, { params: { rowVersion } });
  },
};

// ============================================================================
// Tasks
// ============================================================================

export interface TaskListItemDto {
  id: string;
  clientId: string;
  clientName: string;
  projectId?: string | null;
  projectName?: string | null;
  taskTypeId: string;
  taskTypeName: string;
  title: string;
  description?: string | null;
  status: 'Open' | 'Closed';
  createdUserId: string;
  createdUserName?: string | null;
  closedUserId?: string | null;
  closedUserName?: string | null;
  closedAt?: string | null;
  totalHours: number;
  isActive: boolean;
  rowVersion: number;
  createdAt: string;
  updatedAt?: string | null;
}

export interface UserHoursDto {
  userId: string;
  userName?: string | null;
  hours: number;
}

export interface TimeEntryDto {
  id: string;
  /** Null for ad-hoc (task-less) entries. */
  taskId?: string | null;
  /** Task title — populated when taskId is set. */
  taskTitle?: string | null;
  userId: string;
  userName?: string | null;
  entryDate: string;
  hours: number;
  notes?: string | null;
  /** Ad-hoc fields — populated when taskId is null. */
  clientId?: string | null;
  clientName?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  narration?: string | null;
  rowVersion: number;
  createdAt: string;
  updatedAt?: string | null;
}

export interface TaskDetailDto extends TaskListItemDto {
  hoursByUser: UserHoursDto[];
  timeEntries: TimeEntryDto[];
  statusHistory?: StatusHistoryEntryDto[];
}

export interface CreateTaskRequest {
  clientId: string;
  projectId?: string | null;
  taskTypeId: string;
  title: string;
  description?: string | null;
}

export interface UpdateTaskRequest extends CreateTaskRequest {
  rowVersion: number;
}

export interface TaskQuery extends PageQuery {
  clientId?: string;
  projectId?: string;
  taskTypeId?: string;
  status?: 'Open' | 'Closed';
  createdUserId?: string;
}

export const taskApi = {
  list: async (q: TaskQuery = {}) => {
    const res = await axiosClient.get<ApiEnvelope<PagedResult<TaskListItemDto>>>('/v1/tasks', { params: q });
    return res.data.data;
  },
  getById: async (id: string) => {
    const res = await axiosClient.get<ApiEnvelope<TaskDetailDto>>(`/v1/tasks/${id}`);
    return res.data.data;
  },
  create: async (req: CreateTaskRequest) => {
    const res = await axiosClient.post<ApiEnvelope<TaskDetailDto>>('/v1/tasks', req);
    return res.data.data;
  },
  update: async (id: string, req: UpdateTaskRequest) => {
    const res = await axiosClient.put<ApiEnvelope<TaskDetailDto>>(`/v1/tasks/${id}`, req);
    return res.data.data;
  },
  close: async (id: string, rowVersion: number) => {
    const res = await axiosClient.patch<ApiEnvelope<TaskDetailDto>>(`/v1/tasks/${id}/close`, null, { params: { rowVersion } });
    return res.data.data;
  },
  reopen: async (id: string, rowVersion: number) => {
    const res = await axiosClient.patch<ApiEnvelope<TaskDetailDto>>(`/v1/tasks/${id}/reopen`, null, { params: { rowVersion } });
    return res.data.data;
  },
  remove: async (id: string, rowVersion: number) => {
    await axiosClient.delete(`/v1/tasks/${id}`, { params: { rowVersion } });
  },
};

// ============================================================================
// TimeEntries (nested under tasks for create/list)
// ============================================================================

export interface CreateTimeEntryRequest {
  entryDate: string; // ISO date (YYYY-MM-DD)
  hours: number;
  notes?: string | null;
}

/** Quick-log payload: no task, optional client/project, required narration. */
export interface CreateAdhocTimeEntryRequest {
  entryDate: string; // ISO date (YYYY-MM-DD)
  hours: number;
  narration: string;
  clientId?: string | null;
  projectId?: string | null;
  notes?: string | null;
}

export interface UpdateTimeEntryRequest extends CreateTimeEntryRequest {
  rowVersion: number;
  /** Ad-hoc fields — only applied when the entry has no parent task. */
  narration?: string | null;
  clientId?: string | null;
  projectId?: string | null;
}

export interface TimeEntryQuery extends PageQuery {
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
}

/** Filters for the "My Timesheet" list. */
export interface MyTimesheetQuery extends PageQuery {
  dateFrom?: string;
  dateTo?: string;
  clientId?: string;
  projectId?: string;
  /** 'task' | 'adhoc' | undefined (all) */
  kind?: 'task' | 'adhoc';
}

export const timeEntryApi = {
  listForTask: async (taskId: string, q: TimeEntryQuery = {}) => {
    const res = await axiosClient.get<ApiEnvelope<PagedResult<TimeEntryDto>>>(
      `/v1/tasks/${taskId}/time-entries`, { params: q });
    return res.data.data;
  },
  createForTask: async (taskId: string, req: CreateTimeEntryRequest) => {
    const res = await axiosClient.post<ApiEnvelope<TimeEntryDto>>(
      `/v1/tasks/${taskId}/time-entries`, req);
    return res.data.data;
  },
  /** Quick log: create a time entry without a parent task. */
  createAdhoc: async (req: CreateAdhocTimeEntryRequest) => {
    const res = await axiosClient.post<ApiEnvelope<TimeEntryDto>>('/v1/time-entries', req);
    return res.data.data;
  },
  /** Recent ad-hoc entries for the current user. */
  listMyAdhoc: async (take = 20) => {
    const res = await axiosClient.get<ApiEnvelope<TimeEntryDto[]>>(
      '/v1/time-entries/my-adhoc', { params: { take } });
    return res.data.data;
  },
  /** All entries owned by the current user (task-bound + ad-hoc), server-paged. */
  listMine: async (q: MyTimesheetQuery = {}) => {
    const res = await axiosClient.get<ApiEnvelope<PagedResult<TimeEntryDto>>>(
      '/v1/time-entries/mine', { params: q });
    return res.data.data;
  },
  update: async (id: string, req: UpdateTimeEntryRequest) => {
    const res = await axiosClient.put<ApiEnvelope<TimeEntryDto>>(`/v1/time-entries/${id}`, req);
    return res.data.data;
  },
  remove: async (id: string, rowVersion: number) => {
    await axiosClient.delete(`/v1/time-entries/${id}`, { params: { rowVersion } });
  },
};
